import { http } from './http.ts';
import {
  cameraFromAdonis,
  paginateCameras,
  unwrapCameraListPayload,
  type AdonisCameraRaw,
} from './adonisAdapter.ts';
import type {
  ApiSuccess,
  Camera,
  CameraListQuery,
  CameraType,
  CameraStatus,
  ManualRecordingStatus,
  RecordingMode,
  PaginatedResult,
  RecordingSchedulePutBody,
  RecordingScheduleResponse,
} from './types.ts';

const PATH = '/api/cameras';

export interface CameraCreateInput {
  name: string;
  rtspUrl: string;
  groupId?: number | null;
  type?: CameraType;
  recordingMode?: RecordingMode;
  recordingEnabled?: boolean;
  status?: CameraStatus;
  isFavorite?: boolean;
}

export type CameraUpdateInput = Partial<CameraCreateInput>;

function mapCamera(data: unknown): Camera {
  return cameraFromAdonis(data as AdonisCameraRaw);
}

export const camerasApi = {
  async list(
    query: CameraListQuery = {},
    signal?: AbortSignal,
  ): Promise<PaginatedResult<Camera>> {
    const res = await http.get<ApiSuccess<unknown>>(PATH, {
      params: stripUndefined(query),
      signal,
    });
    const payload = res.data.data;
    if (
      payload &&
      typeof payload === 'object' &&
      !Array.isArray(payload) &&
      Array.isArray((payload as PaginatedResult<AdonisCameraRaw>).items)
    ) {
      const paginated = payload as PaginatedResult<AdonisCameraRaw>;
      return {
        items: paginated.items.map(mapCamera),
        total: paginated.total,
        page: paginated.page,
        pageSize: paginated.pageSize,
      };
    }
    const rows = unwrapCameraListPayload(payload).map(mapCamera);
    return paginateCameras(rows, query);
  },

  async get(id: string, signal?: AbortSignal): Promise<Camera> {
    const res = await http.get<ApiSuccess<AdonisCameraRaw>>(`${PATH}/${id}`, {
      params: { view: 'flashback' },
      signal,
    });
    return mapCamera(res.data.data);
  },

  async create(input: CameraCreateInput): Promise<Camera> {
    const res = await http.post<ApiSuccess<AdonisCameraRaw>>(PATH, toAdonisWriteBody(input));
    return mapCamera(res.data.data);
  },

  async update(id: string, input: CameraUpdateInput): Promise<Camera> {
    const res = await http.put<ApiSuccess<AdonisCameraRaw>>(
      `${PATH}/${id}`,
      toAdonisWriteBody(input),
    );
    return mapCamera(res.data.data);
  },

  async remove(id: string): Promise<void> {
    await http.delete(`${PATH}/${id}`);
  },

  async setStatus(id: string, status: 'active' | 'inactive'): Promise<Camera> {
    const res = await http.patch<ApiSuccess<AdonisCameraRaw>>(`${PATH}/${id}/status`, { status });
    return mapCamera(res.data.data);
  },

  async setFavorite(id: string, isFavorite: boolean): Promise<Camera> {
    const res = await http.patch<ApiSuccess<AdonisCameraRaw>>(`${PATH}/${id}/favorite`, {
      isFavorite,
    });
    return mapCamera(res.data.data);
  },

  async getRecordingSchedule(
    id: string,
    signal?: AbortSignal,
  ): Promise<RecordingScheduleResponse> {
    const res = await http.get<ApiSuccess<RecordingScheduleResponse>>(
      `${PATH}/${id}/recording-schedule`,
      { signal },
    );
    return res.data.data;
  },

  async putRecordingSchedule(
    id: string,
    body: RecordingSchedulePutBody,
    signal?: AbortSignal,
  ): Promise<RecordingScheduleResponse> {
    const res = await http.put<ApiSuccess<RecordingScheduleResponse>>(
      `${PATH}/${id}/recording-schedule`,
      body,
      { signal },
    );
    return res.data.data;
  },

  async getManualRecording(id: string, signal?: AbortSignal): Promise<ManualRecordingStatus> {
    const res = await http.get<ApiSuccess<ManualRecordingStatus>>(
      `${PATH}/${id}/recording/manual`,
      { signal },
    );
    return res.data.data;
  },

  async startManualRecording(id: string, signal?: AbortSignal): Promise<ManualRecordingStatus> {
    const res = await http.post<ApiSuccess<ManualRecordingStatus>>(
      `${PATH}/${id}/recording/manual/start`,
      undefined,
      { signal },
    );
    return res.data.data;
  },

  async stopManualRecording(id: string, signal?: AbortSignal): Promise<ManualRecordingStatus> {
    const res = await http.post<ApiSuccess<ManualRecordingStatus>>(
      `${PATH}/${id}/recording/manual/stop`,
      undefined,
      { signal },
    );
    return res.data.data;
  },
};

function toAdonisWriteBody(input: CameraCreateInput | CameraUpdateInput): Record<string, unknown> {
  const body: Record<string, unknown> = { ...input };
  if (input.rtspUrl !== undefined) {
    body.cam360_rtsp = input.rtspUrl;
    body.streaming_url = input.rtspUrl;
    delete body.rtspUrl;
  }
  if (input.recordingMode !== undefined) {
    body.recording_type = input.recordingMode === 'manual' ? 'continuous' : input.recordingMode;
    delete body.recordingMode;
  }
  if (input.status !== undefined) {
    body.is_active = input.status === 'active';
    delete body.status;
  }
  if (input.groupId !== undefined) {
    body.group_id = input.groupId;
    delete body.groupId;
  }
  return stripUndefinedKeys(body);
}

function stripUndefined<T extends object>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== '' && v !== null) {
      (out as Record<string, unknown>)[k] = v;
    }
  }
  return out;
}

function stripUndefinedKeys<T extends object>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) (out as Record<string, unknown>)[k] = v;
  }
  return out;
}
